import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, TrendingUp, TrendingDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StockCard({ stock }) {
  const isPositive = stock.change_pct >= 0;
  const changeColor = isPositive ? "text-green-500" : "text-red-500";

  return (
    <Card className="p-4 hover:border-blue-500 transition-colors" data-testid="stock-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-lg">{stock.symbol.replace(".NS", "")}</h3>
        <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold">₹{stock.ltp}</span>
          <span className={`text-sm font-semibold ${changeColor}`}>
            {isPositive ? "+" : ""}{stock.change_pct}%
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
          <div>
            <p>Open</p>
            <p className="text-white font-semibold">₹{stock.open}</p>
          </div>
          <div>
            <p>High</p>
            <p className="text-white font-semibold">₹{stock.high}</p>
          </div>
          <div>
            <p>Low</p>
            <p className="text-white font-semibold">₹{stock.low}</p>
          </div>
          <div>
            <p>Volume</p>
            <p className="text-white font-semibold">{(stock.volume / 1000000).toFixed(2)}M</p>
          </div>
        </div>

        <div className="pt-2 flex items-center justify-between">
          {isPositive ? (
            <TrendingUp className="h-4 w-4 text-green-500" />
          ) : (
            <TrendingDown className="h-4 w-4 text-red-500" />
          )}
          <Badge variant="outline" className="text-xs">
            {stock._is_fallback ? "Sample" : "Live"}
          </Badge>
        </div>
      </div>
    </Card>
  );
}

export default function StockGrid() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/market/quotes`);
      setQuotes(res.data.quotes || []);
      toast.success(`Loaded ${res.data.count} stocks`);
    } catch (err) {
      toast.error("Failed to fetch market data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Link to="/">
              <Button variant="ghost" size="sm" className="mb-2">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-4xl font-bold">All Stocks</h1>
            <p className="text-gray-400 mt-1">Live market data for 20 Indian equities</p>
          </div>
          <Button onClick={fetchQuotes} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>

        {loading && quotes.length === 0 ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
            <p className="text-gray-400 mt-2">Loading stock data...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {quotes.map(stock => (
              <StockCard key={stock.symbol} stock={stock} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
